import { useState, useEffect, useCallback, useMemo, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchInput as FplSearchInput } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { RecipeType } from '@/enums/recipe-type.enum';
import { I18N_KEYS } from '@/language/keys';
import { FolderScope } from '@/enums/folder-scope.enum';
import { log } from '@/logger/logger';
import { FlexCenVer, FlexCenVerSpaceBetween, FlexCol, FlexColCenHorVer } from '@/UI/styles';
import { Input } from '@/UI/Input/Input';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { SearchIcon } from '@/UI/Icons/SearchIcon';
import { AppWindowIcon } from '@/UI/Icons/AppWindowIcon';
import { UsersIcon } from '@/UI/Icons/UsersIcon';
import { FolderEmptyIcon } from '@/UI/Icons/FolderEmptyIcon';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { DashboardSection } from '@/components/Dashboard/enums/shared';
import { useGlobalStore } from '@/state/global.state';
import { useUserStore } from '@/state/user.state';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { FileIcon } from '@/UI/Icons/FileIcon';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import {
  useCreateRecipe,
  useDeleteRecipe,
  useDeleteTool,
  useDuplicateRecipe,
  useRenameRecipe,
} from '@/queries/useRecipes';
import { useCreateFolder, useRenameFolder, useMoveFolder, useDeleteFolder, useMoveRecipe } from '@/queries/useFolders';
import { useToolViewQuery } from '@/queries/useToolView';
import { getApiErrorMessage } from '@/utils/api.utils';
import { useAuditTrailCardMenuItem } from '@/hooks/useAuditTrailCardMenuItem';
import { useGoToWorkflow, opensTool } from '@/hooks/useGoToWorkflow';
import { toolWorkflowPath } from '@/utils/recipes';
import { useFigmaAccountLinkPrompt } from '@/components/Nodes/FigmaNode/useFigmaAccountLinkPrompt';
import { isFigmaNodeNotConnectedError, isMoveToPersonalNotOwnerError } from '@/api/errors';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToolTabsEnabled } from '@/hooks/useToolTabsEnabled';
import { FF_APP_VIEW_SEPARATION } from '@/consts/featureFlags';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { TextStdMd } from '@/UI/AppTypography/AppTypography';
import { MenuAction } from '../Menu/Actions';
import { ErrorDisplay } from '../Common/Error/ErrorDisplay';
import { EmptyState } from '../Common/EmptyState/EmptyState';
import { countFilesInFolder, dashboardDisplayName, dashboardRowId, tagWorkflowKinds } from './utils/dashboard.utils';
import DashboardListView from './DashboardListView';
import EducationGallery from './EducationGallery';
import { DashboardCardView } from './DashboardCardView';
import ChangeOwnerModal from './ChangeOwner/ChangeOwnerModal';
import { CreateFolderModal } from './CreateFolderModal';
import { MoveModal } from './MoveModal';
import { Breadcrumbs } from './Breadcrumbs';
import { ViewLayoutSelector } from './ViewLayoutSelector';
import { DashboardScopeTabs } from './DashboardScopeTabs';
import type { DashboardPage } from '@/components/Dashboard/utils/dashboardSection.utils';
import type { DashboardRecipe } from '@/types/recipes';
import type { FolderResponseDto } from '@/types/folder.types';

interface DashboardMainProps {
  recipes: DashboardRecipe[];
  title: string;
  showCase: DashboardRecipe[];
  section: DashboardSection;
  isError: boolean;
  onReload: () => void;
  onSectionChange?: (section: DashboardSection) => void;
  onFolderNavigate?: (folderId?: string | null) => void;
  showChangeNameButton?: boolean;
  currentFolderId?: string | null;
  folders?: FolderResponseDto[];
  folderPath?: FolderResponseDto[];
  /** Which kind this page lists. Set only while `dashboard-tool-tabs` is on. */
  page?: DashboardPage;
  onPageChange?: (page: DashboardPage) => void;
  /** Team and enterprise. Hides the Workspace scope tab otherwise. */
  showWorkspaceScope?: boolean;
}

type MoveDialogState =
  | { kind: 'closed' }
  | { kind: 'workflow'; id: string; name: string; folderId: string | null }
  | { kind: 'folder'; id: string; name: string; folderId: string | null };
type ChangeOwnerDialogState = { kind: 'closed' } | { kind: 'open'; id: string };

export type DashboardItem = 'folder' | 'workflow';

export type EditingNameState = { kind: 'closed' } | { kind: 'editing'; id: string };

export function DashboardMain({
  recipes: workflows,
  title,
  showCase,
  section,
  isError,
  onReload,
  onSectionChange,
  onFolderNavigate,
  showChangeNameButton = false,
  currentFolderId,
  folders = [],
  folderPath = [],
  page,
  onPageChange,
  showWorkspaceScope = false,
}: DashboardMainProps) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { updateSnackbarData } = useGlobalStore();
  const { track } = useAnalytics();
  const goToWorkflow = useGoToWorkflow();
  const [isListView, setIsListView] = useState<boolean>(() => {
    const saved = localStorage.getItem('isListView');
    return saved !== null ? saved === 'true' : false;
  });

  const [worfklowEditState, setWorkflowEditState] = useState<EditingNameState>({ kind: 'closed' });
  const [folderEditState, setFolderEditState] = useState<EditingNameState>({ kind: 'closed' });
  const [changeOwnerDialogState, setChangeOwnerDialogState] = useState<ChangeOwnerDialogState>({ kind: 'closed' });
  const [isShowingCreateFolderDialog, setIsShowingCreateFolderDialog] = useState(false);
  const [moveDialogState, setMoveDialogState] = useState<MoveDialogState>({ kind: 'closed' });
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number | null;
    mouseY: number | null;
    isOpen: boolean;
    recipeId: string | null;
    /** Row identity — `recipeId` alone can't tell a both-ways share's two rows apart. */
    rowId: string | null;
    isFolder: boolean;
  }>({ mouseX: null, mouseY: null, isOpen: false, recipeId: null, rowId: null, isFolder: false });
  const [searchTerm, setSearchTerm] = useState('');
  // Workflows and Tools share this mounted list, so a section change never happens when the page
  // does. Reset during render: an effect would paint one frame of the other page behind the old query.
  const [renderedPage, setRenderedPage] = useState(page);
  if (renderedPage !== page) {
    setRenderedPage(page);
    if (searchTerm) setSearchTerm('');
  }
  const [pendingFolderId, setPendingFolderId] = useState<string | null>();
  const { t } = useTranslation();
  const currentUserId = useUserStore((state) => state.user?.uid);
  const { mutate: createWorkflow } = useCreateRecipe();
  const { mutateAsync: duplicateWorkflow } = useDuplicateRecipe();
  const { promptLinkAccount } = useFigmaAccountLinkPrompt();
  const { mutateAsync: deleteWorkflow } = useDeleteRecipe();
  const { mutateAsync: deleteTool } = useDeleteTool();
  const { mutateAsync: renameWorkflow } = useRenameRecipe();
  const getAuditTrailMenuItems = useAuditTrailCardMenuItem({ section });
  const isWorkspaceFiles = section === DashboardSection.WorkspaceFiles;
  const isSharedWithMe = section === DashboardSection.SharedWithMe;
  // Reachable only while the flag is off, where the nav still offers it. Both shared sections show
  // someone else's files, so Delete means "dismiss my share", not "move my file to trash".
  const isSharedApps = section === DashboardSection.SharedApps;
  const isSharedSection = isSharedApps || isSharedWithMe;
  const { value: isToolViewEnabled } = useFeatureFlag(FF_APP_VIEW_SEPARATION);
  // Trash does not render here — `DashboardArchive` has no tool pointer — so every caller of this
  // list tags rows once the kind pages are on. `page` then keeps a single kind.
  const areKindPagesEnabled = useToolTabsEnabled();
  const pageKind = areKindPagesEnabled ? page : undefined;

  const currentScope = isWorkspaceFiles ? FolderScope.WORKSPACE : FolderScope.PERSONAL;
  const activeFolderId = pendingFolderId !== undefined ? pendingFolderId : currentFolderId;

  // Mutation hooks
  const { mutateAsync: createFolderMutation } = useCreateFolder();
  const { mutateAsync: renameFolderMutation } = useRenameFolder();
  const { mutateAsync: moveFolderMutation } = useMoveFolder();
  const { mutateAsync: deleteFolderMutation } = useDeleteFolder();
  const { mutateAsync: moveRecipeMutation } = useMoveRecipe();

  const { openConfirmationDialog } = useConfirmationDialogStore();

  useEffect(() => {
    localStorage.setItem('isListView', isListView.toString());
  }, [isListView]);

  // Clear search term when section changes
  useEffect(() => {
    setSearchTerm('');
  }, [section]);

  useEffect(() => {
    if (pendingFolderId !== undefined && currentFolderId === pendingFolderId) {
      setPendingFolderId(undefined);
    }
  }, [currentFolderId, pendingFolderId]);

  const handleDragAndDropMove = useCallback(
    async (itemId: string, itemType: DashboardItem, targetFolderId: string) => {
      if (itemType === 'workflow') {
        const movedRecipe = workflows.find((r) => r.id === itemId);
        if (!movedRecipe) return;

        try {
          await moveRecipeMutation({
            id: itemId,
            folderId: targetFolderId,
            scope: currentScope,
            sourceScope: currentScope,
          });
        } catch (error) {
          updateSnackbarData({
            text: `${t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.ERROR_PREFIX)} ${getApiErrorMessage(error, `Failed to move ${itemType}`)}`,
            isOpen: true,
            icon: null,
            severity: 'error',
          });
        }
      } else {
        const movedFolder = folders?.find((f) => f.id === itemId);
        if (!movedFolder) return;

        const isDescendant = (parentId: string | null | undefined, childId: string): boolean => {
          if (!parentId) return false;
          if (parentId === childId) return true;
          const parent = folders?.find((f) => f.id === parentId);
          return parent ? isDescendant(parent.parentId, childId) : false;
        };

        if (isDescendant(targetFolderId, itemId)) {
          updateSnackbarData({
            text: 'Cannot move a folder into its own subfolder',
            isOpen: true,
            icon: null,
          });
          return;
        }

        try {
          await moveFolderMutation({
            id: itemId,
            parentId: targetFolderId,
            scope: currentScope,
            sourceScope: currentScope,
          });
        } catch (error) {
          updateSnackbarData({
            text: `${t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.ERROR_PREFIX)} ${getApiErrorMessage(error, `Failed to move ${itemType}`)}`,
            isOpen: true,
            icon: null,
            severity: 'error',
          });
        }
      }
    },
    [currentScope, workflows, folders, moveRecipeMutation, moveFolderMutation, updateSnackbarData, t],
  );

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(evt.target.value);

  const isSearching = searchTerm.length > 0;

  // The active folder (or the root, as `null`) and every folder beneath it. Built once per change:
  // the filters below run per row, and a per-row tree walk froze the page for large libraries.
  const folderIdsInSearchScope = useMemo(() => {
    if (!isSearching) {
      return null;
    }
    const childIdsByParentId = new Map<string | null, string[]>();
    for (const folder of folders) {
      const parentId = folder.parentId || null;
      const siblings = childIdsByParentId.get(parentId);
      if (siblings) {
        siblings.push(folder.id);
      } else {
        childIdsByParentId.set(parentId, [folder.id]);
      }
    }
    const rootId = activeFolderId ?? null;
    const scope = new Set<string | null>([rootId]);
    const pending: (string | null)[] = [rootId];
    while (pending.length > 0) {
      const parentId = pending.pop() ?? null;
      for (const childId of childIdsByParentId.get(parentId) ?? []) {
        if (!scope.has(childId)) {
          scope.add(childId);
          pending.push(childId);
        }
      }
    }
    return scope;
  }, [isSearching, folders, activeFolderId]);

  // Helper function to search in nested folders
  const searchInNestedFolders = (folders: FolderResponseDto[], searchTerm: string): FolderResponseDto[] => {
    if (!folderIdsInSearchScope) {
      // No search - only show folders directly in current directory
      return folders.filter((folder) => folder.parentId === activeFolderId);
    }

    const normalizedSearchTerm = searchTerm.toLowerCase();
    // Show matching folders whose parentId is in scope (i.e., they are within the current folder tree)
    return folders.filter(
      (folder) =>
        folderIdsInSearchScope.has(folder.parentId || null) && folder.name.toLowerCase().includes(normalizedSearchTerm),
    );
  };

  const taggedWorkflows = useMemo(
    () => tagWorkflowKinds(workflows, areKindPagesEnabled, currentUserId),
    [workflows, areKindPagesEnabled, currentUserId],
  );

  // Deleting a tool is its creator's call, not the workflow owner's (they part after Change Owner), and
  // only the tool view knows the creator. Fetched for the Tool row the menu is open on.
  const contextMenuToolId = useMemo(() => {
    if (!areKindPagesEnabled || isSharedSection || !contextMenu.isOpen || contextMenu.isFolder) return undefined;
    const row = taggedWorkflows.find((workflow) =>
      contextMenu.rowId ? dashboardRowId(workflow) === contextMenu.rowId : workflow.id === contextMenu.recipeId,
    );
    return row?.workflowKind === 'tool' ? row.toolId : undefined;
  }, [areKindPagesEnabled, isSharedSection, contextMenu, taggedWorkflows]);
  const { data: contextMenuToolView } = useToolViewQuery(contextMenuToolId);

  // A page is one kind. Off the flag `page` is unset and every row stays, untagged.
  const kindFilteredWorkflows = pageKind
    ? taggedWorkflows.filter((workflow) => workflow.workflowKind === pageKind)
    : taggedWorkflows;

  // Tools have no folders: a tool that lives in one still shows here, at the root.
  const isFlatToolPage = pageKind === 'tool';
  const shownFolderId = isFlatToolPage ? null : activeFolderId;
  const shownFolderPath = isFlatToolPage ? [] : folderPath;

  // A kind page narrows this level only; leaving the folder stays the search's job, and the two compose.
  const normalizedSearchTerm = searchTerm.toLowerCase();

  // Filter recipes by current folder and search term. Tools ignore the folder.
  const filteredWorkflows = kindFilteredWorkflows.filter((recipe) => {
    const matchesSearch =
      !isSearching ||
      [dashboardDisplayName(recipe), recipe.name].some((name) => name.toLowerCase().includes(normalizedSearchTerm));
    if (!matchesSearch) {
      return false;
    }
    if (isFlatToolPage) {
      return true;
    }
    if (folderIdsInSearchScope) {
      // When searching, include recipes from current folder AND all nested subfolders
      if (shownFolderId) {
        return !!recipe.folderId && folderIdsInSearchScope.has(recipe.folderId);
      }
      // At root with search - search in all recipes
      return !recipe.folderId || folderIdsInSearchScope.has(recipe.folderId);
    }
    // No search - only show recipes directly in current folder/location
    if (shownFolderId) {
      return recipe.folderId === shownFolderId;
    }
    return !recipe.folderId;
  });
  const filteredFolders = isSharedSection || isFlatToolPage ? [] : searchInNestedFolders(folders || [], searchTerm);

  const handleContextMenu = (
    evt: React.MouseEvent<HTMLDivElement>,
    id: string,
    itemType?: DashboardItem,
    rowId?: string,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();

    // Don't show context menu in Shared with Me section unless it's for a specific recipe
    if (isSharedWithMe && !id) {
      return;
    }

    // Use the provided itemType if available, otherwise check if this ID belongs to a folder
    const isFolder = itemType === 'folder' || (!itemType && (folders?.some((folder) => folder.id === id) || false));

    const newContextMenu = {
      mouseX: evt.clientX - 2,
      mouseY: evt.clientY - 4,
      isOpen: true,
      recipeId: id,
      rowId: rowId ?? null,
      isFolder,
    };

    setContextMenu(newContextMenu);
  };

  const handleGeneralContextMenu = (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.preventDefault();
    setContextMenu({
      mouseX: evt.clientX - 2,
      mouseY: evt.clientY - 4,
      isOpen: true,
      recipeId: null,
      rowId: null,
      isFolder: false,
    });
  };

  const handleContextMenuClose = useCallback(() => {
    setContextMenu({ mouseX: null, mouseY: null, isOpen: false, recipeId: null, rowId: null, isFolder: false });
  }, []);

  const handleGoToMyFiles = useCallback(() => {
    // The copy is a new private workflow. Workflows and Tools share RecentRecipes, so the page has
    // to follow or this opens My tools, where the copy is not listed.
    onPageChange?.('workflow');
    onSectionChange?.(DashboardSection.RecentRecipes);
  }, [onPageChange, onSectionChange]);

  const handleDuplicateWorkflow = useCallback(
    async (id: string, options?: { showSuccessToast?: boolean }) => {
      try {
        await duplicateWorkflow(id);
        // The copy is a workflow on My workflows. Leave Tools, Shared, or Workspace so it is on screen.
        const alreadyOnMyWorkflows = page === 'workflow' && section === DashboardSection.RecentRecipes;
        if (pageKind && !alreadyOnMyWorkflows) {
          handleGoToMyFiles();
        }
        if (options?.showSuccessToast) {
          updateSnackbarData({
            text: t(pageKind ? I18N_KEYS.DASHBOARD.DUPLICATE_SUCCESS_WORKFLOW : I18N_KEYS.DASHBOARD.DUPLICATE_SUCCESS),
            isOpen: true,
            action: pageKind
              ? undefined
              : {
                  text: t(I18N_KEYS.DASHBOARD.DUPLICATE_SUCCESS_ACTION),
                  onClick: handleGoToMyFiles,
                },
            icon: <CheckMarkCircleIcon height={20} width={20} />,
          });
        }
      } catch (error) {
        if (isFigmaNodeNotConnectedError(error)) {
          promptLinkAccount(I18N_KEYS.RECIPE_MAIN.NODES.FIGMA_NODE.DUPLICATE_ERROR_NOT_CONNECTED);
          return;
        }
        log.error('error duplicating recipe: ', error);
        updateSnackbarData({
          text: getApiErrorMessage(error, t(I18N_KEYS.DASHBOARD.DUPLICATE_ERROR)),
          isOpen: true,
          icon: null,
          severity: 'error',
        });
      }
    },
    [duplicateWorkflow, handleGoToMyFiles, page, pageKind, promptLinkAccount, section, t, updateSnackbarData],
  );

  const enableRecipeNameEdit = useCallback(
    (id: string) => {
      const recipeToRename = workflows?.filter((rcp) => rcp.id === id);
      if (recipeToRename.length) {
        setFolderEditState({ kind: 'closed' });
        setWorkflowEditState({ kind: 'editing', id });
      }
    },
    [workflows],
  );

  const enableFolderNameEdit = useCallback(
    (id: string) => {
      const folderToRename = folders?.find((folder) => folder.id === id);
      if (folderToRename) {
        setWorkflowEditState({ kind: 'closed' });
        setFolderEditState({ kind: 'editing', id });
      }
    },
    [folders],
  );

  const handleRenameWorkflow = useCallback(
    async (id: string, newName: string) => {
      try {
        await renameWorkflow({ id, newName });
        setWorkflowEditState({ kind: 'closed' });
      } catch (error) {
        log.error('error renaming recipe: ', error);
        updateSnackbarData({
          text: getApiErrorMessage(error, 'Error renaming recipe'),
          isOpen: true,
          icon: null,
          severity: 'error',
        });
      }
    },
    [renameWorkflow, updateSnackbarData],
  );

  const handleRenameFolder = useCallback(
    async (id: string, newName: string) => {
      try {
        await renameFolderMutation({ id, name: newName, scope: currentScope });
        setFolderEditState({ kind: 'closed' });
      } catch (error) {
        log.error('error renaming folder: ', error);
        updateSnackbarData({
          text: t(I18N_KEYS.MAIN_DASHBOARD.RENAME_FOLDER_ERROR),
          isOpen: true,
          icon: null,
          severity: 'error',
        });
      }
    },
    [renameFolderMutation, updateSnackbarData, t, currentScope],
  );

  const handleCancelRenameWorkflow = useCallback(() => {
    setWorkflowEditState({ kind: 'closed' });
  }, []);

  const handleCancelRenameFolder = useCallback((_id: string) => {
    setFolderEditState({ kind: 'closed' });
  }, []);

  const handleFolderNavigate = useCallback(
    (folderId?: string | null) => {
      setWorkflowEditState({ kind: 'closed' });
      setFolderEditState({ kind: 'closed' });
      setPendingFolderId(folderId ?? null);
      setSearchTerm('');
      onFolderNavigate?.(folderId);
    },
    [onFolderNavigate],
  );

  const handleConfirmDelete = useCallback(
    async ({ id, kind }: { id: string; kind: DashboardItem }) => {
      if (kind === 'folder') {
        try {
          await deleteFolderMutation({ id, scope: currentScope });
          track('dashboard_item_deleted', { item_type: 'folder', source: section }, TrackTypeEnum.BI);
          updateSnackbarData({
            text: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_FOLDER_SUCCESS),
            isOpen: true,
            icon: <CheckMarkCircleIcon height={20} width={20} />,
            severity: 'success',
          });
        } catch (error) {
          log.error('error deleting folder: ', error);
          updateSnackbarData({
            text: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_FOLDER_ERROR),
            isOpen: true,
            icon: null,
            severity: 'error',
          });
        }
      } else if (kind === 'workflow') {
        try {
          await deleteWorkflow({
            // `DesignApp` is what useDeleteRecipe reads as "hide the share"; `File` deletes the
            // workflow itself, which a recipient is not allowed to do (the server 403s).
            id,
            type: isSharedSection ? RecipeType.DesignApp : RecipeType.File,
          });
          track('dashboard_item_deleted', { item_type: 'workflow', source: section }, TrackTypeEnum.BI);
          updateSnackbarData({
            text: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_WORKFLOW_SUCCESS),
            isOpen: true,
            icon: <CheckMarkCircleIcon height={20} width={20} />,
            severity: 'success',
          });
        } catch (error) {
          log.error('error deleting recipe: ', error);
          updateSnackbarData({
            text: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_WORKFLOW_ERROR),
            isOpen: true,
            icon: null,
            severity: 'error',
          });
        }
      }
    },
    [currentScope, deleteFolderMutation, deleteWorkflow, isSharedSection, section, t, track, updateSnackbarData],
  );

  const onDeleteWorkflow = useCallback(
    (id: string) => {
      const workflowName = workflows.find((r) => r.id === id)?.name ?? '';
      const bold = { bold: <TextStdMd color={token.text} /> };
      // In a shared section Delete dismisses the caller's own share: it drops one
      // `user_shared_recipes` row and leaves the owner's file untouched. The trash wording promises
      // the opposite — a restore, from a Trash section, of something that was never deleted. Behind
      // the flag, because off it the "Tools" section still carries its own wording and the sections
      // must read exactly as they do in production today.
      const isShareDismissal = areKindPagesEnabled && isSharedSection;
      openConfirmationDialog({
        title: isShareDismissal
          ? t(I18N_KEYS.MAIN_DASHBOARD.DISMISS_SHARE_DIALOG.TITLE)
          : isSharedApps
            ? t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRMATION_DELETE_APP)
            : t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.TITLE),
        message:
          !isShareDismissal && isSharedApps ? t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.MSG_DELETE_APP) : undefined,
        messageData: isShareDismissal
          ? {
              i18n: {
                i18nKey: I18N_KEYS.MAIN_DASHBOARD.DISMISS_SHARE_DIALOG.MSG,
                values: { item_name: workflowName },
              },
              data: bold,
            }
          : isSharedApps
            ? undefined
            : {
                i18n: {
                  i18nKey: I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.MSG,
                  values: { item_type: 'file', item_name: workflowName },
                },
                data: bold,
              },
        confirmBtnText: isShareDismissal
          ? t(I18N_KEYS.MAIN_DASHBOARD.DISMISS_SHARE_DIALOG.CONFIRM)
          : isSharedApps
            ? t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRM_DELETE)
            : t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRM),
        width: !isShareDismissal && isSharedApps ? 'sm' : 'md',
        onConfirm: () => handleConfirmDelete({ id, kind: 'workflow' }),
      });
    },
    [workflows, openConfirmationDialog, isSharedApps, isSharedSection, areKindPagesEnabled, t, handleConfirmDelete],
  );

  // A Tool row shares its workflow's `id`, so the workflow delete would trash both; this one leaves the
  // workflow in place. No Trash entry to restore from, hence "Delete" rather than "Move to trash".
  const onDeleteTool = useCallback(
    ({ id, toolId, name }: { id: string; toolId: string; name: string }) => {
      openConfirmationDialog({
        title: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRMATION_DELETE_APP),
        messageData: {
          i18n: { i18nKey: I18N_KEYS.MAIN_DASHBOARD.DELETE_TOOL_DIALOG.MSG, values: { item_name: name } },
          data: { bold: <TextStdMd color={token.text} /> },
        },
        confirmBtnText: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRM_DELETE),
        width: 'md',
        onConfirm: async () => {
          try {
            await deleteTool({ toolId, workflowId: id });
            track('dashboard_item_deleted', { item_type: 'tool', source: section }, TrackTypeEnum.BI);
            updateSnackbarData({
              text: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_TOOL_SUCCESS),
              isOpen: true,
              icon: <CheckMarkCircleIcon height={20} width={20} />,
              severity: 'success',
            });
          } catch (error) {
            log.error('error deleting tool: ', error);
            updateSnackbarData({
              text: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_TOOL_ERROR),
              isOpen: true,
              icon: null,
              severity: 'error',
            });
          }
        },
      });
    },
    [deleteTool, openConfirmationDialog, section, t, track, updateSnackbarData],
  );

  const onDeleteFolder = useCallback(
    (id: string) => {
      const folderName = folders.find((f) => f.id === id)?.name ?? '';
      openConfirmationDialog({
        title: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.TITLE),
        messageData: {
          i18n: {
            i18nKey: I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.MSG,
            values: { item_type: 'folder', item_name: folderName },
          },
          data: { bold: <TextStdMd color={token.text} /> },
        },
        confirmBtnText: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRM),
        width: 'md',
        onConfirm: () => handleConfirmDelete({ id, kind: 'folder' }),
      });
    },
    [folders, handleConfirmDelete, openConfirmationDialog, t],
  );

  const handleCreateFolder = async (folderName: string) => {
    try {
      await createFolderMutation({ name: folderName, scope: currentScope, parentId: activeFolderId });
      setIsShowingCreateFolderDialog(false);
      updateSnackbarData({
        text: t(I18N_KEYS.MAIN_DASHBOARD.CREATE_FOLDER_MODAL.SUCCESS),
        isOpen: true,
        icon: <CheckMarkCircleIcon height={20} width={20} />,
        severity: 'success',
      });
      track('create_folder_clicked', { source: 'dashboard_main_context_menu' }, TrackTypeEnum.BI);
    } catch (error) {
      updateSnackbarData({
        text: `${t(I18N_KEYS.MAIN_DASHBOARD.CREATE_FOLDER_MODAL.ERROR_PREFIX)} ${getApiErrorMessage(error, 'Failed to create folder')}`,
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    }
  };

  const handleMove = async (targetFolderId: string | null, targetScope: FolderScope) => {
    if (moveDialogState.kind === 'closed') {
      return;
    }

    try {
      if (moveDialogState.kind === 'folder') {
        await moveFolderMutation({
          id: moveDialogState.id,
          parentId: targetFolderId,
          scope: targetScope,
          sourceScope: currentScope,
        });
        updateSnackbarData({
          text: t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.SUCCESS_FOLDER),
          isOpen: true,
          icon: <CheckMarkCircleIcon height={20} width={20} />,
          severity: 'success',
        });
      } else {
        await moveRecipeMutation({
          id: moveDialogState.id,
          folderId: targetFolderId,
          scope: targetScope,
          sourceScope: currentScope,
        });
        updateSnackbarData({
          text: t(
            pageKind === 'tool'
              ? I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.SUCCESS_TOOL
              : pageKind === 'workflow'
                ? I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.SUCCESS_WORKFLOW
                : I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.SUCCESS_FILE,
          ),
          isOpen: true,
          icon: <CheckMarkCircleIcon height={20} width={20} />,
          severity: 'success',
        });
      }
    } catch (error) {
      updateSnackbarData({
        text: isMoveToPersonalNotOwnerError(error)
          ? t(
              moveDialogState.kind === 'folder'
                ? I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.NOT_OWNER_FOLDER
                : I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.NOT_OWNER_FILE,
            )
          : `${t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.ERROR_PREFIX)} ${getApiErrorMessage(error, 'Failed to move item')}`,
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    } finally {
      setMoveDialogState({ kind: 'closed' });
    }
  };

  // Create menu items function that will be called fresh each time
  const getMenuItems = useCallback(
    (menuContext?: typeof contextMenu): MenuAction[] => {
      const currentContext = menuContext || contextMenu;
      const items: MenuAction[] = [];

      // Create stays off Tools: a workflow made there never appears on the page. Shared is
      // someone else's list, so it has no create either.
      if (!isSharedSection && !isFlatToolPage && !currentContext.isFolder && !currentContext.recipeId) {
        items.push({
          biEventKey: { menu: MENU_NAMES.DASHBOARD_MAIN, action: 'create_file' },
          name: t(
            pageKind
              ? I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CREATE_WORKFLOW
              : I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CREATE_FILE,
          ),
          action: () => {
            const scope = section === DashboardSection.WorkspaceFiles ? FolderScope.WORKSPACE : FolderScope.PERSONAL;
            createWorkflow({ openInNewTab: false, parentId: shownFolderId, scope });
            handleContextMenuClose();
          },
          icon: <FileIcon width={16} height={16} />,
        });
        items.push({
          biEventKey: { menu: MENU_NAMES.DASHBOARD_MAIN, action: 'create_folder' },
          name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CREATE_FOLDER),
          action: () => {
            setIsShowingCreateFolderDialog(true);
            handleContextMenuClose();
          },
          icon: <FolderEmptyIcon width={16} height={16} />,
        });
      }

      // Add folder-specific options if a folder is selected
      if (currentContext.recipeId && currentContext.isFolder) {
        const folder = folders.find((f) => f.id === currentContext.recipeId);

        if (items.length > 0) {
          items.push({ type: 'divider' });
        }

        // Add Open options for folders
        items.push(
          {
            biEventKey: { menu: MENU_NAMES.DASHBOARD_FOLDER, action: 'open' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN),
            action: () => {
              if (handleFolderNavigate && folder) {
                handleFolderNavigate(folder.id);
              }
              handleContextMenuClose();
            },
          },
          {
            biEventKey: { menu: MENU_NAMES.DASHBOARD_FOLDER, action: 'open_new_tab' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN_NEW_TAB),
            action: () => {
              if (folder) {
                window.open(`/folders/${folder.id}`, '_blank');
              }
              handleContextMenuClose();
            },
          },
        );

        items.push({ type: 'divider' });

        // Add Rename for folders
        if (!isSharedSection) {
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_FOLDER, action: 'rename' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.RENAME),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              enableFolderNameEdit(currentContext.recipeId);
              handleContextMenuClose();
            },
          });
        }

        // Add Move for folders
        if (!isSharedSection) {
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_FOLDER, action: 'move' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.MOVE),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              setMoveDialogState({
                kind: 'folder',
                id: currentContext.recipeId,
                name: folder?.name || '',
                folderId: folder?.parentId ?? null,
              });
              handleContextMenuClose();
            },
          });
        }

        // Add Delete for folders
        if (!isSharedSection) {
          items.push({ type: 'divider' });
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_FOLDER, action: 'move_to_trash' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.MOVE_TO_TRASH),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              onDeleteFolder(currentContext.recipeId);
              handleContextMenuClose();
            },
          });
        }
      }

      // Add recipe-specific options if a recipe is selected (not a folder)
      if (currentContext.recipeId && !currentContext.isFolder) {
        // By row, not by id: a both-ways share has two rows under one id that differ in what "Open"
        // opens and whether the dismiss applies (WEA-6479). Tagged rows, since that is what renders.
        const selectedRecipe = taggedWorkflows.find((workflow) =>
          currentContext.rowId
            ? dashboardRowId(workflow) === currentContext.rowId
            : workflow.id === currentContext.recipeId,
        );
        const isRecipeOwner = selectedRecipe?.owner && selectedRecipe.owner === currentUserId;
        const openTarget = selectedRecipe ?? { id: currentContext.recipeId };

        // Add divider if we already have items
        if (items.length > 0) {
          items.push({ type: 'divider' });
        }

        // Add Open options
        items.push(
          {
            biEventKey: { menu: MENU_NAMES.DASHBOARD_WORKFLOW, action: 'open' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN),
            action: () => {
              goToWorkflow(openTarget);
              handleContextMenuClose();
            },
          },
          {
            biEventKey: { menu: MENU_NAMES.DASHBOARD_WORKFLOW, action: 'open_new_tab' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN_NEW_TAB),
            action: () => {
              goToWorkflow(openTarget, true);
              handleContextMenuClose();
            },
          },
        );

        // Offers the destination the click does not take, but only where it exists: a tool grant is
        // independent of the workflow, so a tool-only share would just 403 on /flow. Via the canvas,
        // since plain /flow would redirect these recipients back to /tool (WEA-5088). Behind the tabs
        // flag too — `opensTool` alone is `app-view-separation`, already at 100%.
        const canReachWorkflow = !!selectedRecipe?.sharedAsWorkflow || !!isRecipeOwner;
        if (areKindPagesEnabled && opensTool(selectedRecipe, isToolViewEnabled) && canReachWorkflow) {
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_TOOL, action: 'open_workflow' },
            name: t(I18N_KEYS.TOOL_VIEW.OPEN_WORKFLOW),
            action: () => {
              if (currentContext.recipeId) {
                window.location.href = toolWorkflowPath(currentContext.recipeId);
              }
              handleContextMenuClose();
            },
          });
        }

        // A tool shared without the workflow cannot be copied, and the server rejects it. A workspace
        // teammate can: membership is enough to view that workflow. On the kind pages the copy is a
        // workflow, and the label says so. Tools offers none of this: there is no copy, no folder to
        // move into, no rename or delete, and no owner or audit action on the tool itself.
        const isToolOnlyShare = isSharedSection && !isRecipeOwner && selectedRecipe?.sharedAsWorkflow === false;
        const showDuplicate = !isToolOnlyShare && !isFlatToolPage;
        const showMove = !isSharedSection && !isFlatToolPage;
        // Rename edits the workflow's name. Not on the owner's Tool row when it shows the tool's own
        // name: the Workflow row beside it shows and renames that. The tool's name is edited in Tool details.
        const isOwnersRenamedToolRow =
          isRecipeOwner && !!selectedRecipe?.displayName && selectedRecipe.displayName !== selectedRecipe.name;
        const showRename = !isSharedSection && !isOwnersRenamedToolRow && !isFlatToolPage;
        if (showDuplicate || showMove || showRename) {
          items.push({ type: 'divider' });
        }
        if (showDuplicate) {
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_WORKFLOW, action: 'duplicate' },
            name: t(
              areKindPagesEnabled
                ? I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.DUPLICATE_WORKFLOW
                : I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.DUPLICATE,
            ),
            action: () => {
              handleContextMenuClose();
              void handleDuplicateWorkflow(currentContext.recipeId ?? '', {
                showSuccessToast:
                  section !== DashboardSection.RecentRecipes && section !== DashboardSection.WorkspaceFiles,
              });
            },
          });
        }

        if (showMove) {
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_WORKFLOW, action: 'move' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.MOVE),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              setMoveDialogState({
                kind: 'workflow',
                id: currentContext.recipeId,
                name: selectedRecipe ? dashboardDisplayName(selectedRecipe) : '',
                folderId: selectedRecipe?.folderId ?? null,
              });
              handleContextMenuClose();
            },
          });
        }

        if (showRename) {
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_WORKFLOW, action: 'rename' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.RENAME),
            action: () => {
              enableRecipeNameEdit(currentContext.recipeId ?? '');
              handleContextMenuClose();
            },
          });
        }

        if (!isFlatToolPage) {
          items.push(...getAuditTrailMenuItems(selectedRecipe));
        }

        // Add Change Owner for workspace files or when showChangeNameButton is true
        const isWorkspaceFile = section === DashboardSection.WorkspaceFiles;

        if (!isFlatToolPage && (showChangeNameButton || (isWorkspaceFile && isRecipeOwner)) && !isSharedSection) {
          items.push({ type: 'divider' });
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_WORKFLOW, action: 'change_owner' },
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CHANGE_OWNER),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              setChangeOwnerDialogState({ kind: 'open', id: currentContext.recipeId });
              handleContextMenuClose();
            },
          });
        }

        // Delete means move-to-trash in the caller's own sections and "dismiss this share" in the
        // shared ones. Our only dismiss hides a `user_shared_recipes` row, so it belongs to workflow
        // rows: on a tool row it would revoke workflow access rather than the tool (WEA-6479).
        const isWorkflowShareRow = selectedRecipe?.workflowKind
          ? selectedRecipe.workflowKind === 'workflow'
          : (selectedRecipe?.sharedAsWorkflow ?? !selectedRecipe?.toolId);
        const canShowDeleteInCurrentSection = isSharedSection ? isWorkflowShareRow : !isWorkspaceFile || isRecipeOwner;
        // On a Tool row, Delete takes the tool alone, and only its creator gets it.
        const rowTool =
          areKindPagesEnabled && !isSharedSection && selectedRecipe?.workflowKind === 'tool' && selectedRecipe.toolId
            ? { id: selectedRecipe.id, toolId: selectedRecipe.toolId, name: dashboardDisplayName(selectedRecipe) }
            : undefined;
        if (!isFlatToolPage && rowTool) {
          const toolView = contextMenuToolView?.toolId === rowTool.toolId ? contextMenuToolView : undefined;
          if (toolView?.isOwner) {
            items.push({ type: 'divider' });
            items.push({
              biEventKey: { menu: MENU_NAMES.DASHBOARD_TOOL, action: 'delete' },
              name: t(I18N_KEYS.GENERAL.DELETE),
              action: () => {
                onDeleteTool({ ...rowTool, name: toolView.name || rowTool.name });
                handleContextMenuClose();
              },
            });
          }
        } else if (!isFlatToolPage && canShowDeleteInCurrentSection) {
          items.push({ type: 'divider' });
          items.push({
            biEventKey: { menu: MENU_NAMES.DASHBOARD_WORKFLOW, action: 'remove' },
            name: isSharedSection
              ? t(I18N_KEYS.GENERAL.DELETE)
              : t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.MOVE_TO_TRASH),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              onDeleteWorkflow(currentContext.recipeId);
              handleContextMenuClose();
            },
          });
        }
      }

      return items;
    },
    [
      contextMenu,
      isSharedSection,
      isToolViewEnabled,
      areKindPagesEnabled,
      t,
      section,
      createWorkflow,
      shownFolderId,
      pageKind,
      isFlatToolPage,
      handleContextMenuClose,
      goToWorkflow,
      folders,
      handleFolderNavigate,
      enableFolderNameEdit,
      onDeleteFolder,
      taggedWorkflows,
      currentUserId,
      showChangeNameButton,
      handleDuplicateWorkflow,
      enableRecipeNameEdit,
      onDeleteWorkflow,
      onDeleteTool,
      contextMenuToolView,
      getAuditTrailMenuItems,
    ],
  );

  const renderSubheader = () =>
    workflows?.length || folders?.length || pageKind ? (
      <FlexCenVerSpaceBetween
        data-testid="dashboard-top-menu-container"
        sx={{
          width: '100%',
          minHeight: '40px',
          flexShrink: 0,
          mt: 4,
          mb: 4,
        }}
      >
        {/* Tabs beside the title, which elsewhere doubles as the breadcrumb out of a folder. */}
        <FlexCenVer sx={{ gap: 3, minWidth: 0 }}>
          <Breadcrumbs folderPath={shownFolderPath} onNavigate={handleFolderNavigate} rootName={title} />
          {pageKind ? (
            <DashboardScopeTabs
              page={pageKind}
              section={section}
              showWorkspace={showWorkspaceScope || section === DashboardSection.WorkspaceFiles}
              onChange={(next) => onSectionChange?.(next)}
            />
          ) : null}
        </FlexCenVer>

        <FlexCenVer data-testid="dashboard-list-top-menu" sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ mr: 2, width: '180px' }}>
            {isFplMigrationEnabled ? (
              <FplSearchInput
                aria-label={t(I18N_KEYS.GENERAL.SEARCH)}
                placeholder={t(I18N_KEYS.GENERAL.SEARCH)}
                size="lg"
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
              />
            ) : (
              <Input
                placeholder={t(I18N_KEYS.GENERAL.SEARCH)}
                value={searchTerm}
                onChange={handleSearchChange}
                startAdornment={<SearchIcon width={16} height={16} style={{ flexShrink: 0 }} />}
                sx={{ width: '180px' }}
              />
            )}
          </Box>

          <ViewLayoutSelector isListView={isListView} onChange={setIsListView} />
        </FlexCenVer>
      </FlexCenVerSpaceBetween>
    ) : null;

  const renderContent = () => {
    if (filteredWorkflows.length === 0 && filteredFolders.length === 0) {
      // Check if there's a search with no results (at any level - inside folder or at root)
      if (searchTerm) {
        return (
          <EmptyState
            title={t(I18N_KEYS.MAIN_DASHBOARD.SEARCH_NO_RESULTS.TITLE, { searchTerm })}
            description={t(I18N_KEYS.MAIN_DASHBOARD.SEARCH_NO_RESULTS.DESCRIPTION)}
            actions={[]}
            sx={{ paddingTop: '104px', alignItems: 'flex-start' }}
          />
        );
      }

      // Check if we're inside a folder and it's empty (no search active)
      if (shownFolderId) {
        return (
          <EmptyState
            icon={<FolderEmptyIcon />}
            title={t(I18N_KEYS.MAIN_DASHBOARD.FOLDER_EMPTY.TITLE)}
            description={t(
              pageKind === 'workflow'
                ? I18N_KEYS.MAIN_DASHBOARD.FOLDER_EMPTY.DESCRIPTION_WORKFLOWS
                : I18N_KEYS.MAIN_DASHBOARD.FOLDER_EMPTY.DESCRIPTION,
            )}
            actions={[]}
            sx={{ paddingTop: '104px', alignItems: 'flex-start' }}
          />
        );
      }
    }

    // Nothing of this page's kind at the root. A section that still holds the other kind used to
    // miss the empty state below, because that one asks whether any row exists at all.
    const rootHasNoRowsOfThisKind = isFlatToolPage
      ? kindFilteredWorkflows.length === 0
      : !folders.length && (pageKind ? !kindFilteredWorkflows.length : !taggedWorkflows.length);
    if (rootHasNoRowsOfThisKind) {
      // Keyed by section rather than parallel ternary chains over the same booleans: one entry per
      // section, and any section without one (personal files, folders) takes the default below.
      // "Shared with me" gets no action — filling it isn't the viewer's to do: someone else has to
      // share with them.
      const emptyStateBySection: Partial<Record<DashboardSection, ComponentProps<typeof EmptyState>>> = {
        [DashboardSection.SharedApps]: {
          icon: <AppWindowIcon />,
          title: t(I18N_KEYS.DASHBOARD.PAGES.SHARED_APPS.EMPTY_STATE.TITLE),
          description: null,
          actions: [
            {
              label: t(I18N_KEYS.DASHBOARD.PAGES.SHARED_APPS.EMPTY_STATE.ACTION),
              href: EXTERNAL_LINKS.designAppTutorial,
              target: '_blank',
            },
          ],
        },
        [DashboardSection.SharedWithMe]: {
          icon: <UsersIcon />,
          title: t(
            pageKind === 'tool'
              ? I18N_KEYS.DASHBOARD.PAGES.TOOLS.EMPTY_STATE.SHARED
              : pageKind === 'workflow'
                ? I18N_KEYS.DASHBOARD.PAGES.WORKFLOWS.EMPTY_STATE.SHARED
                : I18N_KEYS.DASHBOARD.PAGES.SHARED_WITH_ME.EMPTY_STATE.TITLE,
          ),
          description: null,
          actions: [],
        },
        [DashboardSection.WorkspaceFiles]: {
          title: t(
            pageKind === 'tool'
              ? I18N_KEYS.DASHBOARD.PAGES.TOOLS.EMPTY_STATE.WORKSPACE
              : pageKind === 'workflow'
                ? I18N_KEYS.DASHBOARD.PAGES.WORKFLOWS.EMPTY_STATE.WORKSPACE
                : I18N_KEYS.DASHBOARD.PAGES.WORKSPACE_FILES.EMPTY_STATE.TITLE,
          ),
          description:
            pageKind === 'tool'
              ? t(I18N_KEYS.DASHBOARD.PAGES.TOOLS.EMPTY_STATE.WORKSPACE_DESCRIPTION)
              : pageKind === 'workflow'
                ? t(I18N_KEYS.DASHBOARD.PAGES.WORKFLOWS.EMPTY_STATE.WORKSPACE_DESCRIPTION)
                : 'Files added here will be shared with all workspace\u00A0members',
          actions: [],
        },
      };
      const myToolsEmpty = pageKind === 'tool' && section === DashboardSection.RecentRecipes;
      const emptyState = myToolsEmpty
        ? {
            icon: <AppWindowIcon />,
            title: t(I18N_KEYS.DASHBOARD.PAGES.TOOLS.EMPTY_STATE.TITLE),
            description: null,
            actions: [],
          }
        : (emptyStateBySection[section] ?? {
            title: t(I18N_KEYS.DASHBOARD.PAGES.FILES.EMPTY_STATE.TITLE),
            description: t(I18N_KEYS.DASHBOARD.PAGES.FILES.EMPTY_STATE.DESCRIPTION),
            actions: [
              {
                label: t(
                  pageKind === 'workflow'
                    ? I18N_KEYS.MAIN_DASHBOARD.CREATE_NEW_WORKFLOW
                    : I18N_KEYS.MAIN_DASHBOARD.CREATE_NEW_FILE,
                ),
                onClick: () => {
                  track('created_new_flow', { source: 'dashboard_empty_state' }, TrackTypeEnum.BI);
                  createWorkflow();
                },
              },
            ],
          });
      return <EmptyState {...emptyState} sx={{ paddingTop: 'max(15%, 144px)', alignItems: 'flex-start' }} />;
    }
    if (isListView) {
      return (
        <DashboardListView
          recipes={filteredWorkflows} // REMOVE THIS WHEN FILTER WILL BE IN THE SERVER
          allRecipes={kindFilteredWorkflows}
          kindFilter={pageKind ?? 'all'}
          folders={filteredFolders}
          allFolders={folders}
          handleRenameRecipe={(id, name) => void handleRenameWorkflow(id, name)}
          handleContextMenu={handleContextMenu}
          recipeEditState={worfklowEditState}
          cancelRename={handleCancelRenameWorkflow}
          onFolderNavigate={handleFolderNavigate}
          currentFolderId={shownFolderId}
          isSearching={isSearching}
          folderEditState={folderEditState}
          handleRenameFolder={handleRenameFolder ? (id, name) => void handleRenameFolder(id, name) : undefined}
          cancelRenameFolder={handleCancelRenameFolder}
          countFilesInFolder={(folderId) =>
            countFilesInFolder(
              folderId,
              folders || [],
              kindFilteredWorkflows.map((workflow) => ({ id: workflow.id, parentId: workflow.folderId })),
            )
          }
          folderCountKind={pageKind === 'workflow' ? 'workflow' : 'file'}
          showFolderCounts={!isFlatToolPage}
          flattenFolders={isFlatToolPage}
          onMoveItem={(itemId, itemType, targetFolderId) => {
            void handleDragAndDropMove(itemId, itemType, targetFolderId);
          }}
          isWorkspaceFiles={isWorkspaceFiles || isSharedWithMe}
        />
      );
    }
    return (
      <DashboardCardView
        recipes={filteredWorkflows}
        allRecipes={kindFilteredWorkflows}
        folders={filteredFolders}
        allFolders={folders}
        handleRenameRecipe={(id, name) => void handleRenameWorkflow(id, name)}
        handleContextMenu={handleContextMenu}
        recipeEditState={worfklowEditState}
        cancelRename={handleCancelRenameWorkflow}
        onFolderNavigate={handleFolderNavigate}
        currentFolderId={shownFolderId}
        isSearching={isSearching}
        folderEditState={folderEditState}
        handleRenameFolder={handleRenameFolder ? (id, name) => void handleRenameFolder(id, name) : undefined}
        cancelRenameFolder={handleCancelRenameFolder}
        countFilesInFolder={(folderId) =>
          countFilesInFolder(
            folderId,
            folders || [],
            kindFilteredWorkflows.map((workflow) => ({ id: workflow.id, parentId: workflow.folderId })),
          )
        }
        folderCountKind={pageKind === 'workflow' ? 'workflow' : 'file'}
        flattenFolders={isFlatToolPage}
        onMoveItem={(itemId, itemType, targetFolderId) => {
          void handleDragAndDropMove(itemId, itemType, targetFolderId);
        }}
        isWorkspaceFiles={isWorkspaceFiles || isSharedWithMe}
      />
    );
  };

  return (
    <>
      <Box
        component="main"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          pb: 0,
        }}
        onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
          // Don't show context menu in Shared with Me section
          if (isSharedWithMe) {
            return;
          }

          // Handle right-click on the main container or any empty space
          const target = e.target as HTMLElement;
          const isEmptySpace =
            target === e.currentTarget ||
            target.closest('[data-testid="dashboard-table-view-container"]') ||
            target.closest('[data-testid="dashboard-card-view-container"]') ||
            target.closest('.MuiGrid2-root');

          if (isEmptySpace && !target.closest('[role="button"]') && !target.closest('[data-testid^="recipe-card-"]')) {
            handleGeneralContextMenu(e);
          }
        }}
      >
        {showCase?.length && !isSharedApps && pageKind !== 'tool' ? (
          <Box data-testid="showcase-recipes-container" sx={{ width: '100%', pt: 4 }}>
            <EducationGallery recipes={showCase} />
          </Box>
        ) : null}

        {isError ? (
          <FlexColCenHorVer sx={{ width: '100%', height: '100%' }}>
            <ErrorDisplay
              title={t(I18N_KEYS.MAIN_DASHBOARD.ERROR_LOADING_WORKFLOWS.TITLE)}
              description={null}
              actions={[
                { label: t(I18N_KEYS.MAIN_DASHBOARD.ERROR_LOADING_WORKFLOWS.ACTION), onClick: () => onReload?.() },
              ]}
            />
          </FlexColCenHorVer>
        ) : (
          <FlexCol
            sx={{ flexGrow: 1 }}
            onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
              // Handle right-click on empty space within the content area
              const target = e.target as HTMLElement;
              const isCard =
                target.closest('[data-testid^="recipe-card-"]') ||
                target.closest('[data-testid^="folder-card-"]') ||
                target.closest('tr[role="checkbox"]');

              if (!isCard) {
                e.stopPropagation();
                handleGeneralContextMenu(e);
              }
            }}
          >
            {renderSubheader()}
            {renderContent()}
          </FlexCol>
        )}
      </Box>
      <AppContextMenu
        key={`${contextMenu.rowId ?? contextMenu.recipeId}-${contextMenu.isFolder}`}
        mouseX={contextMenu.mouseX}
        mouseY={contextMenu.mouseY}
        open={(() => {
          if (!contextMenu.isOpen) return false;
          const items = getMenuItems(contextMenu);
          return items.length > 0;
        })()}
        onClose={handleContextMenuClose}
        items={useMemo(() => {
          if (!contextMenu.isOpen) return [];
          return getMenuItems(contextMenu);
        }, [contextMenu, getMenuItems])}
        width="200px"
      />
      {changeOwnerDialogState.kind === 'open' && (
        <ChangeOwnerModal
          open
          onClose={() => setChangeOwnerDialogState({ kind: 'closed' })}
          recipeId={changeOwnerDialogState.id}
        />
      )}
      <CreateFolderModal
        open={isShowingCreateFolderDialog}
        onClose={() => setIsShowingCreateFolderDialog(false)}
        onConfirm={handleCreateFolder}
      />
      {moveDialogState.kind !== 'closed' && (
        <MoveModal
          open
          onClose={() => setMoveDialogState({ kind: 'closed' })}
          onConfirm={handleMove}
          itemName={moveDialogState.name}
          itemId={moveDialogState.id}
          isFolder={moveDialogState.kind === 'folder'}
          titleKey={
            moveDialogState.kind === 'folder'
              ? undefined
              : pageKind === 'tool'
                ? I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.TITLE_TOOL
                : pageKind === 'workflow'
                  ? I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.TITLE_WORKFLOW
                  : undefined
          }
          currentFolderId={moveDialogState.folderId}
          isWorkspaceScope={isWorkspaceFiles}
        />
      )}
    </>
  );
}
